<template>
    <div v-if="companyVerifoneConfig"
         id="verifone-settings"
         class="modl-tab-body">
        <div class="modl-body">
            <form class="form-h"
                  @submit.prevent="submit">
                <div v-if="saveError"
                     class="alert alert-danger">
                    <p>{{ saveError }}</p>
                </div>

                <div class="form-group">
                    <label class="control-label">
                        {{ l('VERIFONE_SETTINGS.REPRINT') }}
                    </label>
                    <div class="form-h__right">
                        <div>
                            <button class="btn btn-sm btn-default"
                                    @click.prevent="reprintMerchantReceipt()">
                                {{ l('VERIFONE_SETTINGS.MERCHANT_RECEIPT') }}
                            </button>

                            &nbsp;

                            <button class="btn btn-sm btn-default"
                                    @click.prevent="reprintCardholderReceipt()">
                                {{ l('VERIFONE_SETTINGS.CUSTOMER_RECEIPT') }}
                            </button>
                        </div>
                    </div>
                </div>

                <hr />

                <div class="form-group">
                    <label for="verifone-integration-type"
                           class="control-label">
                        {{ l('VERIFONE_SETTINGS.TERMINAL_TYPE') }}
                    </label>
                    <select id="verifone-integration-type"
                            v-model="deviceVerifoneConfig.integrationType"
                            class="form-control input-lg"
                            @change="integrationTypeChanged">
                        <option v-for="type in integrationTypes"
                                :key="type.value"
                                :value="type.value">
                            {{ type.label }}
                        </option>
                    </select>
                </div>
                <div class="form-group">
                    <label for="verifone-terminal-host"
                           class="control-label">
                        {{ l('VERIFONE_SETTINGS.TERMINAL_IP') }}
                    </label>
                    <input id="verifone-terminal-host"
                           v-model="deviceVerifoneConfig.terminalHost"
                           :disabled="deviceVerifoneConfig.integrationType === VerifoneIntegrationType.POS_CLIENT"
                           type="text"
                           class="form-control input-lg">
                </div>

                <div class="form-group">
                    <label for="verifone-manager-pin"
                           class="control-label">
                        {{ l('VERIFONE_SETTINGS.MANAGER_PIN') }}
                    </label>
                    <div class="form-h__right">
                        <input id="verifone-manager-pin"
                               v-model="deviceVerifoneConfig.managerPin"
                               type="text"
                               class="form-control input-lg">
                        <PaymentConfigDefaultValue config-key="managerPin"
                                                   :device-config="deviceVerifoneConfig"
                                                   :company-config="companyVerifoneConfig" />
                    </div>
                </div>

                <div class="form-group">
                    <label for="verifone-user-id"
                           class="control-label">
                        {{ l('VERIFONE_SETTINGS.USER_ID') }}
                    </label>
                    <div class="form-h__right">
                        <input id="verifone-user-id"
                               v-model="deviceVerifoneConfig.userId"
                               type="text"
                               class="form-control input-lg">
                        <PaymentConfigDefaultValue config-key="userId"
                                                   :device-config="deviceVerifoneConfig"
                                                   :company-config="companyVerifoneConfig" />
                    </div>
                </div>

                <div class="form-group">
                    <label for="verifone-user-pin"
                           class="control-label">
                        {{ l('VERIFONE_SETTINGS.USER_PIN') }}
                    </label>
                    <div class="form-h__right">
                        <input id="verifone-user-pin"
                               v-model="deviceVerifoneConfig.userPin"
                               type="text"
                               class="form-control input-lg">
                        <PaymentConfigDefaultValue config-key="userPin"
                                                   :device-config="deviceVerifoneConfig"
                                                   :company-config="companyVerifoneConfig" />
                    </div>
                </div>

                <hr />

                <div class="form-group">
                    <label class="control-label">
                        {{ l('VERIFONE_SETTINGS.EXTENDED_RECEIPTS') }}
                    </label>
                    <div class="form-h__right">
                        <div class="btn-group">
                            <button type="button"
                                    :class="{'active': !deviceVerifoneConfig.extendedReceipts}"
                                    class="btn btn-sm btn-default"
                                    @click.prevent="setExtendedReceipts(false)">
                                {{ l('VERIFONE_SETTINGS.OFF_BTN') }}
                            </button>
                            <button type="button"
                                    :class="{'active': deviceVerifoneConfig.extendedReceipts}"
                                    class="btn btn-sm btn-default"
                                    @click.prevent="setExtendedReceipts(true)">
                                {{ l('VERIFONE_SETTINGS.ON_BTN') }}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <div class="modl-footer">
            <!-- Cancel button -->
            <button class="btn btn-lg btn-default cancel-btn"
                    @click.prevent="$emit('cancel')">
                {{ l('CANCEL') }}
            </button>

            <!-- OK button -->
            <button class="btn btn-lg btn-primary"
                    :disabled="saving"
                    @click.prevent="submit">
                <i v-if="saving"
                   class="fa-solid fa-spinner fa-spin" />
                <template v-else>
                    {{ l('SAVE') }}
                </template>
            </button>
        </div>
    </div>
</template>

<script>
import PaymentConfigDefaultValue from '@/epos/vue/modals/Settings/components/PaymentConfigDefaultValue';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import VerifoneConfig from '@/lib/PaymentMethods/Verifone/VerifoneConfig';
import VerifoneIntegrationType from '@/lib/PaymentMethods/Verifone/VerifoneIntegrationType';
import { apiResponseToError } from '@/lib/Api';
import { ConfirmTabAwayMixin } from '@/vue/mixins/ConfirmTabAwayMixin';
import { container } from '@/container';

/**
 * This uses some non-translated strings because it's really for internal use only.
 */
export default {
    name: 'VerifoneSettingsTab',

    components: { PaymentConfigDefaultValue },

    mixins: [
        ConfirmTabAwayMixin,
    ],

    emits: [
        'cancel',
        'resolve',
    ],

    data() {
        return {
            changed: false,

            saving: false,
            saveError: null,

            companyVerifoneConfig: null,

            // **This is the device config only**.
            // Other settings might exist on the company payment method.
            // If a value exists in the company config and is omitted from the device config,
            // the company level setting will be used.
            deviceVerifoneConfig: {
                integrationType: null,
                terminalHost: null,
                managerPin: null,
                userId: null,
                userPin: null,
                extendedReceipts: false, // Default is false
            },

            device: null,
            integrationTypes: [
                {
                    label: 'P400, E285, E355',
                    value: VerifoneIntegrationType.ENGAGE,
                },
                {
                    label: 'VX820 (POS Client/Ocius)',
                    value: VerifoneIntegrationType.POS_CLIENT,
                },
                {
                    label: 'VX820IP',
                    value: VerifoneIntegrationType.EVO,
                },
            ],
            VerifoneIntegrationType,

            paymentMethodRef: PaymentMethodRef.CARD_VERIFONE_CHIP_AND_PIN,
        };
    },

    created() {
        // Load the payment method.
        const paymentMethod = container.paymentMethodService.getByRef(this.paymentMethodRef);
        if (!paymentMethod) {
            // Throw an error if the payment method is not enabled (how did we get here though?)
            throw new Error('Company does not have Verifone payment method enabled.');
        }

        // Set the existing company-level config in the Vue data.
        // This is for reference in the UI only and will not be modified here.
        this.companyVerifoneConfig = paymentMethod.config ? JSON.parse(paymentMethod.config) : {};

        /**
         * @type {!Device}
         */
        this.device = container.currentDevice.device;

        // Copy any existing values from the device's paymentTerminalConfig.verifone object
        // to the new config (this.deviceVerifoneConfig).
        if (this.device.paymentTerminalConfig?.[this.paymentMethodRef]) {
            Object.entries(this.device.paymentTerminalConfig[this.paymentMethodRef])
                .forEach(([key, value]) => {
                    if (value || value === false) {
                        this.deviceVerifoneConfig[key] = value;
                    }
                });
        }

        // Now add a watcher for changes to the settings.
        this.$watch(
            'deviceVerifoneConfig',
            () => {
                this.changed = true;
            },
            { deep: true },
        );
    },

    methods: {
        reprintMerchantReceipt() {
            container.verifoneDriver.reprintMerchantReceipt();
        },

        reprintCardholderReceipt() {
            container.verifoneDriver.reprintCardholderReceipt();
        },

        setExtendedReceipts(value) {
            this.deviceVerifoneConfig.extendedReceipts = value;
        },

        integrationTypeChanged() {
            // Clear the terminal IP if changing to POS Client because it could lead to problems if you leave
            // an IP in here. POS Client should always be at 127.0.0.1, which is the default.
            if (this.deviceVerifoneConfig.integrationType === VerifoneIntegrationType.POS_CLIENT) {
                this.deviceVerifoneConfig.terminalHost = null;
            }
        },

        async submit() {
            if (this.saving) {
                return;
            }

            this.saving = true;
            this.saveError = null;

            // Build the new config object from the form data.
            const newVerifoneConfig = {};
            Object.keys(this.deviceVerifoneConfig).forEach((key) => {
                if (this.deviceVerifoneConfig[key] || this.deviceVerifoneConfig[key] === false) {
                    newVerifoneConfig[key] = this.deviceVerifoneConfig[key];
                }
            });

            console.log('Saving new Verifone config.', newVerifoneConfig);

            // Validate the new config.
            try {
                const configInstance = new VerifoneConfig();
                container.hydrator.hydrate(newVerifoneConfig, configInstance);
                configInstance.validate();
            } catch (e) {
                this.saveError = e.message;
                this.saving = false;

                return;
            }

            try {
                // Set the new print extended receipts setting in the Verifone Driver.
                // This is in a catch because if the existing config is invalid, accessing the verifone driver
                // will throw an error.
                container.verifoneDriver.setExtendedReceipts(this.deviceVerifoneConfig.extendedReceipts);
            } catch (e) {
                console.warn(
                    'Failed to call setExtendedReceipts.'
                        + ' This may indicate a problem with the OLD config but not necessarily the NEW config.'
                        + ' Refresh ProPoint and try again.',
                    e
                );
            }

            // Send the new settings to the API to be saved on the device.
            // Remember, we only want to update the Verifone settings not anything else that is stored in the config,
            // so the new value starts as the existing value and we only replace one key.
            let newDevicePaymentTerminalConfig = this.device.paymentTerminalConfig;

            // In case there is no existing JSON in the paymentTerminalConfig.
            if (!newDevicePaymentTerminalConfig) {
                newDevicePaymentTerminalConfig = {};
            }
            // In case there is no existing Verifone key in the paymentTerminalConfig.
            if (!newDevicePaymentTerminalConfig.hasOwnProperty(this.paymentMethodRef)) {
                newDevicePaymentTerminalConfig[this.paymentMethodRef] = {};
            }
            newDevicePaymentTerminalConfig[this.paymentMethodRef] = newVerifoneConfig;

            const newDevicePaymentTerminalConfigJson = JSON.stringify(newDevicePaymentTerminalConfig);

            container.deviceResource.setPaymentTerminalConfig(
                {
                    deviceID: this.device.ID,
                },
                {
                    paymentTerminalConfig: newDevicePaymentTerminalConfigJson,
                },
                () => {
                    // Set the new values on the current device so they are populated if the settings modal is reopened,
                    // without having to reload the app.
                    /**
                     * @type {CurrentDevice}
                     */
                    const currentDevice = container.currentDevice;
                    // Convert it back from JSON instead of using the object so that it is a clone and doesn't
                    // contain anything like Vue's reactivity.
                    currentDevice.device.paymentTerminalConfig = JSON.parse(newDevicePaymentTerminalConfigJson);

                    // Save the device to storage.
                    currentDevice.cache();

                    this.saving = false;

                    container.toastFactory.success(
                        'VERIFONE_SETTINGS.SUCCESS_TOAST_TITLE',
                        'You may need to <strong>sync two times</strong> for changes to take effect.',
                    );

                    this.changed = false;
                    this.$emit('resolve');
                },
                (result) => {
                    this.saveError = apiResponseToError(result).message;
                    this.saving = false;
                },
            );
        },

        hasUnsavedChanges() {
            return this.changed;
        },
    },
};
</script>
